import { useState, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import type { QuoteRow } from "@/hooks/useQuotes";
import { useRecordPaymentForm } from "@/components/quotes/useRecordPaymentForm";
import { RECORD_PAYMENT_METHODS } from "@/components/quotes/paymentMethods";
import { formatDay, formatMoney, type PaymentSummary } from "./drawerLogic";

interface RecordPaymentPanelProps {
  quote: QuoteRow;
  summary: PaymentSummary;
  onClose: () => void;
}

const fieldCls = "grid gap-[3px] text-[12px] text-muted-foreground";
const selectCls = "flex h-8 w-full rounded-md border border-input bg-background px-2 text-sm text-foreground";

function Warn({ children }: { children: ReactNode }) {
  return <div role="alert" className="grid gap-2 rounded-lg bg-status-warn-soft px-2.5 py-2 text-[13px] font-semibold text-status-warn">{children}</div>;
}

/**
 * Record a payment inline (#1559), on RecordPaymentDialog's form hook: same
 * insert, card-fee split and Square duplicate check. Starts on E-transfer
 * with the balance filled in, as the prototype does.
 */
export function RecordPaymentPanel({ quote, summary, onClose }: RecordPaymentPanelProps) {
  const f = useRecordPaymentForm({
    enabled: true, quoteId: quote.id, quoteTotal: Number(quote.total) || 0, amountPaid: summary.paid,
    initialMethod: "etransfer", onRecorded: onClose,
  });
  // Save payment with a Square match asks first; Record anyway inserts with the override note.
  const [askedDup, setAskedDup] = useState(false);
  const dup = askedDup && f.squareMatch;
  const pendingWarn = f.method === "etransfer" && summary.pendingAmount > 0;
  const fee = f.applyFee && f.feeStatus === "success" ? (f.split.feeCents / 100).toFixed(2) : "";
  const close = () => {
    f.resetAmount();
    onClose();
  };

  return (
    <div className="grid gap-2.5 rounded-lg bg-background p-2.5">
      <h4 className="text-[13.5px] font-semibold">Record a payment you received</h4>
      <div className="grid grid-cols-4 items-end gap-2 max-[760px]:grid-cols-2">
        <label className={fieldCls}>
          Amount
          <Input inputMode="decimal" value={f.displayedAmount} onChange={(e) => { f.setAmount(e.target.value); setAskedDup(false); }}
            className="h-8 text-right tabular-nums text-foreground" />
        </label>
        <label className={fieldCls}>
          Method
          <select value={f.method} onChange={(e) => { f.handleMethodChange(e.target.value); setAskedDup(false); }} className={selectCls}>
            {RECORD_PAYMENT_METHODS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
          </select>
        </label>
        <label className={fieldCls}>
          Date
          <Input type="date" value={f.paymentDate} onChange={(e) => f.setPaymentDate(e.target.value)} className="h-8 text-foreground" />
        </label>
        <label className={fieldCls}>
          Reference #
          <Input value={f.reference} placeholder="Optional" onChange={(e) => f.setReference(e.target.value)} className="h-8 text-foreground" />
        </label>
        {f.isCard && (
          <>
            <label className={fieldCls}>
              Card fee
              <Input readOnly disabled={f.noCardFee} value={f.noCardFee ? "" : fee} placeholder="$0" className="h-8 text-right tabular-nums text-foreground" />
            </label>
            <label className="col-span-3 flex items-center gap-[7px] self-end pb-2 text-[13px] text-foreground max-[760px]:col-span-1">
              <input type="checkbox" checked={f.noCardFee} onChange={(e) => f.setNoCardFee(e.target.checked)} className="h-4 w-4 accent-primary" />
              No card fee was charged
            </label>
          </>
        )}
        <label className={`${fieldCls} col-span-full`}>
          Notes
          <Input value={f.notes} placeholder="Optional" onChange={(e) => f.setNotes(e.target.value)} className="h-8 text-foreground" />
        </label>
      </div>
      {f.applyFee && f.feeStatus === "pending" && <p className="text-[12px] text-muted-foreground">Loading the card fee setting…</p>}
      {f.applyFee && f.feeStatus === "error" && (
        <p className="text-[12px] text-destructive">Couldn't load the card fee setting — reload to record a card payment</p>
      )}
      {f.isCard && f.squarePaymentsError && (
        <p className="text-[12px] text-muted-foreground">Couldn't check whether Square already recorded this payment.</p>
      )}
      {pendingWarn && (
        <Warn>
          Already waiting on {formatMoney(summary.pendingAmount)} by e-transfer. Use Mark received on that row instead, or this counts as a second payment.
        </Warn>
      )}
      {dup ? (
        <Warn>
          {formatMoney(Number(dup.amount) || 0)} by Square on {formatDay(dup.payment_date)} is already recorded. Is this a second payment?
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="outline" size="sm" className="h-8 bg-card text-foreground" onClick={close}>Cancel</Button>
            <Button type="button" size="sm" className="h-8" onClick={f.submit} disabled={f.isPending || f.feeUnavailable}>Record anyway</Button>
          </div>
        </Warn>
      ) : (
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" size="sm" className="h-8 bg-card" onClick={close}>Cancel</Button>
          <Button
            type="button"
            size="sm"
            className="h-8"
            disabled={f.isPending || f.feeUnavailable}
            onClick={() => (f.squareMatch ? setAskedDup(true) : f.submit())}
          >
            Save payment
          </Button>
        </div>
      )}
    </div>
  );
}
